import * as React from 'react';
import cn from 'classnames';
import FlightSelector from '../FlightSelector/FlightSelector';
import { useTheme } from '../../../theme';
import { useUpgradeFlightClass } from '../../hooks/UpgradeFlightClass.hook';
import { useTranslation } from 'react-i18next';

import Toolbar from '../../../Toolbar';
import { RefundSummary } from '../../../RefundSummary';
import { Dispatch, DispatchWithoutAction } from 'react';
import {
	CheckinSaveOrderServicesMutationVariables,
	InfoMessageTypeEnum,
	SaveOrderServicesMutationVariables
} from '@websky/graphql';
import ConfirmOption from '../../../ConfirmOption';
import ModalConfirm from '../../../ModalConfirm';
import Notification from '../../../Notification';
import { isCheckoutOrderGuard } from '../../../typeguards';
import { CdnIcon } from '@shared/ui';
import { CardExtraViewVariant } from '@shared/ui/CardExtraView';

export interface OwnProps {
	contentClassName?: string;
	loading?: boolean;
	onBackClick?: DispatchWithoutAction;
	onSaveServices: Dispatch<SaveOrderServicesMutationVariables> | Dispatch<CheckinSaveOrderServicesMutationVariables>;
	onContinueWithoutSelection?: DispatchWithoutAction;
	showRefundServicesNotification?: boolean;
	mode?: CardExtraViewVariant;
}

const UpgradeFlightClass: React.FC<OwnProps> = ({
	onBackClick,
	contentClassName,
	loading,
	onSaveServices,
	onContinueWithoutSelection,
	showRefundServicesNotification,
	mode = 'default'
}) => {
	const { UpgradeFlightClass: css } = useTheme('UpgradeFlightClass');
	const { t } = useTranslation('UpgradeFlightClass');

	const model = useUpgradeFlightClass({
		onBackClick,
		loading,
		onSaveServices,
		onContinueWithoutSelection,
		showRefundServicesNotification
	});

	const title = React.useMemo(() => {
		return model.step === 'select' ? t('Select segments') : t('Segments for upgrade');
	}, [model.step]);

	const nextButtonText = React.useMemo<string>(() => {
		return model.step === 'select' ? t('Continue') : t('Accept');
	}, [model.step]);

	return (
		<>
			<div className={cn(css.wrapper, contentClassName, { [css.selectStep]: model.step === 'select' })}>
				<ConfirmOption variant={'Upgrade'} service={model.service} />

				<div className={css.flights__wrapper}>
					<div className={css.flights__title}>{t(title)}</div>
					<div className={css.flights}>
						<FlightSelector
							type={model.step}
							selectedSegments={model.selectedSegments}
							onChange={model.toggleSegment}
							flights={model.flights}
							transferInfo={model.transferDurationBySegments}
						/>
					</div>
				</div>

				{model.step === 'review' && (
					<RefundSummary
						flight={isCheckoutOrderGuard(model.order) ? model.order.flight : model.order.segments}
						travellers={model.order.travellers}
						gdsServicesRefund={model.gdsServicesRefund}
						isLoading={model.refundServicesLoading}
						mode={mode}
						notification={
							<Notification
								className={css.notification}
								status={InfoMessageTypeEnum.Warning}
								icon={<CdnIcon icon="Info/Not included" />}
								type="context"
								infoMessage={{
									title: t('servicesCancellationNotification_title'),
									text: t('servicesCancellationNotification_message'),
									type: InfoMessageTypeEnum.Warning,
									code: 'upgradeRestrictions'
								}}
							/>
						}
					/>
				)}
			</div>

			<Toolbar
				nextButtonText={nextButtonText}
				isLoading={model.isServicesSaving}
				totalPrice={model.totalPrice}
				onButtonClick={model.toolbarCallbacks.next}
				onBackButtonClick={model.toolbarCallbacks.back}
				buttonDisabled={model.nextButtonDisabled || (model.refundServicesLoading && model.step === 'review')}
			/>

			<ModalConfirm
				isOpen={model.isRefundNotificationOpen}
				onClose={model.closeRefundNotification}
				onConfirm={model.onModalConfirm}
				onCancel={model.closeRefundNotification}
				title={t('servicesCancellationNotification_title')}
			>
				{t('servicesCancellationNotification_message')}
			</ModalConfirm>
		</>
	);
};

export default UpgradeFlightClass;
